import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  ArrowLeft, 
  Camera, 
  Sparkles, 
  Languages,
  Snowflake,
  TreePine,
  Lightbulb,
  Heart,
  Palette,
  Zap
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import logo from '@/assets/logo.png';
import { Footer } from '@/components/Footer';

const Trends = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const trends = [
    {
      id: 'minimalist',
      icon: Snowflake,
      emoji: '❄️',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
    },
    {
      id: 'rustic',
      icon: TreePine,
      emoji: '🌿',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
    },
    {
      id: 'tech',
      icon: Zap,
      emoji: '✨',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
    },
    {
      id: 'glam',
      icon: Heart,
      emoji: '🎭',
      color: 'text-pink-600',
      bgColor: 'bg-pink-50',
      borderColor: 'border-pink-200',
    },
  ];

  const colorPalette = [
    { name: t('trendsPage.colors.neutral'), color: 'bg-gray-300', textColor: 'text-gray-700' },
    { name: t('trendsPage.colors.gold'), color: 'bg-amber-400', textColor: 'text-amber-700' },
    { name: t('trendsPage.colors.green'), color: 'bg-emerald-600', textColor: 'text-emerald-700' },
    { name: t('trendsPage.colors.red'), color: 'bg-red-600', textColor: 'text-red-700' },
    { name: t('trendsPage.colors.blue'), color: 'bg-blue-500', textColor: 'text-blue-700' },
  ];

  const currentUrl = window.location.hostname.includes('arvorenota10') 
    ? 'https://arvorenota10.com.br/trends'
    : 'https://treerated10.com/trends';

  return (
    <>
      <Helmet>
        <title>{t('trendsPage.meta.title')}</title>
        <meta name="description" content={t('trendsPage.meta.description')} />
        <meta name="keywords" content={t('trendsPage.meta.keywords')} />
        <link rel="canonical" href={currentUrl} />
        
        {/* hreflang tags */}
        <link rel="alternate" hrefLang="en" href="https://treerated10.com/trends" />
        <link rel="alternate" hrefLang="es" href="https://treerated10.com/trends" />
        <link rel="alternate" hrefLang="pt-BR" href="https://arvorenota10.com.br/trends" />
        <link rel="alternate" hrefLang="it" href="https://treerated10.com/trends" />
        <link rel="alternate" hrefLang="de" href="https://treerated10.com/trends" />
        <link rel="alternate" hrefLang="x-default" href="https://treerated10.com/trends" />

        {/* Open Graph */}
        <meta property="og:title" content={t('trendsPage.meta.title')} />
        <meta property="og:description" content={t('trendsPage.meta.description')} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:type" content="article" />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": t('trendsPage.meta.title'),
            "description": t('trendsPage.meta.description'),
            "datePublished": "2025-01-01",
            "dateModified": new Date().toISOString().split('T')[0],
            "author": {
              "@type": "Organization",
              "name": "TreeRated10"
            },
            "publisher": {
              "@type": "Organization",
              "name": "TreeRated10",
              "logo": {
                "@type": "ImageObject",
                "url": "https://treerated10.com/logo.png"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": currentUrl
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-background via-background to-primary/5">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-sm border-b border-border">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('trendsPage.header.back')}
            </Button>

            <img src={logo} alt="TreeRated10" className="h-10 w-10" />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Select language">
                  <Languages className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => changeLanguage('en')}>
                  🇬🇧 English
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage('es')}>
                  🇪🇸 Español
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage('pt')}>
                  🇧🇷 Português
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage('it')}>
                  🇮🇹 Italiano
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => changeLanguage('de')}>
                  🇩🇪 Deutsch
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Hero Section */}
        <section className="max-w-4xl mx-auto px-4 py-12 text-center">
          <div className="inline-block px-4 py-1 bg-primary/20 rounded-full mb-4">
            <span className="text-sm font-semibold text-primary">
              {t('trendsPage.hero.badge')}
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            {t('trendsPage.hero.title')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            {t('trendsPage.hero.subtitle')}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('trendsPage.hero.readTime')}
          </p>
        </section>

        {/* Table of Contents */}
        <nav className="max-w-4xl mx-auto px-4 mb-12">
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4 text-foreground">
              {t('trendsPage.toc.title')}
            </h2>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {['minimalist', 'rustic', 'tech', 'glam', 'colors', 'materials', 'naturalVsArtificial'].map((section) => (
                <li key={section}>
                  <a
                    href={`#${section}`}
                    className="text-primary hover:underline flex items-center gap-2 p-2 rounded hover:bg-primary/5 transition-colors"
                  >
                    <span className="text-lg">•</span>
                    {t(`trendsPage.sections.${section}.title`)}
                  </a>
                </li>
              ))}
            </ul>
          </Card>
        </nav>

        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-4 space-y-16 pb-16">
          {/* Trend Sections */}
          {trends.map((trend) => {
            const TrendIcon = trend.icon;
            return (
              <section key={trend.id} id={trend.id} className="scroll-mt-20">
                <Card className={`p-8 border-2 ${trend.borderColor} ${trend.bgColor}/30`}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`p-3 rounded-full ${trend.bgColor}`}>
                      <TrendIcon className={`h-8 w-8 ${trend.color}`} />
                    </div>
                    <div>
                      <span className="text-4xl">{trend.emoji}</span>
                      <h2 className="text-3xl font-bold text-foreground">
                        {t(`trendsPage.sections.${trend.id}.title`)}
                      </h2>
                    </div>
                  </div>
                  
                  <p className="text-muted-foreground mb-6 leading-relaxed text-lg">
                    {t(`trendsPage.sections.${trend.id}.description`)}
                  </p>

                  <div className="bg-background/50 rounded-lg p-6">
                    <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Lightbulb className="h-5 w-5 text-primary" />
                      {t('trendsPage.tips.title')}
                    </h3>
                    <ul className="space-y-2">
                      {(t(`trendsPage.sections.${trend.id}.tips`, { returnObjects: true }) as string[]).map((tip: string, index: number) => (
                        <li key={index} className="flex items-start gap-2 text-muted-foreground">
                          <span className="text-primary mt-1">✓</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              </section>
            );
          })}

          {/* Colors Section */}
          <section id="colors" className="scroll-mt-20">
            <Card className="p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-full bg-gradient-to-r from-red-500 via-green-500 to-blue-500">
                  <Palette className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-foreground">
                  {t('trendsPage.sections.colors.title')}
                </h2>
              </div>

              <p className="text-muted-foreground mb-6 leading-relaxed text-lg">
                {t('trendsPage.sections.colors.description')}
              </p>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                {colorPalette.map((color, index) => (
                  <div key={index} className="text-center">
                    <div className={`w-full h-24 rounded-lg ${color.color} mb-2 shadow-md`}></div>
                    <span className={`text-sm font-medium ${color.textColor}`}>{color.name}</span>
                  </div>
                ))}
              </div>

              <div className="bg-muted/50 rounded-lg p-6">
                <ul className="space-y-2">
                  {(t('trendsPage.sections.colors.tips', { returnObjects: true }) as string[]).map((tip: string, index: number) => (
                    <li key={index} className="flex items-start gap-2 text-muted-foreground">
                      <span className="text-primary mt-1">✓</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          </section>

          {/* Materials Section */}
          <section id="materials" className="scroll-mt-20">
            <Card className="p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-full bg-amber-100">
                  <Lightbulb className="h-8 w-8 text-amber-600" />
                </div>
                <h2 className="text-3xl font-bold text-foreground">
                  {t('trendsPage.sections.materials.title')}
                </h2>
              </div>

              <p className="text-muted-foreground mb-6 leading-relaxed text-lg">
                {t('trendsPage.sections.materials.description')}
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-muted/50 rounded-lg p-6">
                  <h3 className="font-semibold text-foreground mb-3">
                    {t('trendsPage.sections.materials.sustainable.title')}
                  </h3>
                  <ul className="space-y-2">
                    {(t('trendsPage.sections.materials.sustainable.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                      <li key={index} className="flex items-start gap-2 text-muted-foreground">
                        <span className="text-green-600 mt-1">♻️</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-muted/50 rounded-lg p-6">
                  <h3 className="font-semibold text-foreground mb-3">
                    {t('trendsPage.sections.materials.lighting.title')}
                  </h3>
                  <ul className="space-y-2">
                    {(t('trendsPage.sections.materials.lighting.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                      <li key={index} className="flex items-start gap-2 text-muted-foreground">
                        <span className="text-yellow-600 mt-1">💡</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>
          </section>

          {/* Natural vs Artificial */}
          <section id="naturalVsArtificial" className="scroll-mt-20">
            <Card className="p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 rounded-full bg-green-100">
                  <TreePine className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="text-3xl font-bold text-foreground">
                  {t('trendsPage.sections.naturalVsArtificial.title')}
                </h2>
              </div>

              <p className="text-muted-foreground mb-6 leading-relaxed text-lg">
                {t('trendsPage.sections.naturalVsArtificial.description')}
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 rounded-lg p-6 border-2 border-green-200">
                  <h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                    <TreePine className="h-5 w-5" />
                    {t('trendsPage.sections.naturalVsArtificial.natural.title')}
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-green-600 mb-1">
                        {t('trendsPage.sections.naturalVsArtificial.natural.prosLabel')}
                      </p>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        {(t('trendsPage.sections.naturalVsArtificial.natural.pros', { returnObjects: true }) as string[]).map((pro: string, index: number) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-green-600">+</span>
                            <span>{pro}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-red-600 mb-1">
                        {t('trendsPage.sections.naturalVsArtificial.natural.consLabel')}
                      </p>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        {(t('trendsPage.sections.naturalVsArtificial.natural.cons', { returnObjects: true }) as string[]).map((con: string, index: number) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-red-600">-</span>
                            <span>{con}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-6 border-2 border-blue-200">
                  <h3 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                    <Sparkles className="h-5 w-5" />
                    {t('trendsPage.sections.naturalVsArtificial.artificial.title')}
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-green-600 mb-1">
                        {t('trendsPage.sections.naturalVsArtificial.artificial.prosLabel')}
                      </p>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        {(t('trendsPage.sections.naturalVsArtificial.artificial.pros', { returnObjects: true }) as string[]).map((pro: string, index: number) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-green-600">+</span>
                            <span>{pro}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-red-600 mb-1">
                        {t('trendsPage.sections.naturalVsArtificial.artificial.consLabel')}
                      </p>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        {(t('trendsPage.sections.naturalVsArtificial.artificial.cons', { returnObjects: true }) as string[]).map((con: string, index: number) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-red-600">-</span>
                            <span>{con}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </section>

          {/* CTA Section */}
          <section className="bg-gradient-to-r from-primary/10 to-primary/20 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-foreground mb-4">
              {t('trendsPage.cta.title')}
            </h2>
            <p className="text-muted-foreground mb-6">
              {t('trendsPage.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/quiz')}
                size="lg"
                className="bg-primary hover:opacity-90"
              >
                <Sparkles className="mr-2 h-5 w-5" />
                {t('trendsPage.cta.quiz')}
              </Button>
              <Button
                onClick={() => navigate('/camera')}
                size="lg"
                variant="outline"
              >
                <Camera className="mr-2 h-5 w-5" />
                {t('trendsPage.cta.evaluate')}
              </Button>
            </div>
          </section>
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default Trends;
